import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';

export type SocialAuthLayout = 'stacked' | 'compact';

interface SocialAuthButtonsProps {
    /** `compact` = circular icon row; `stacked` = full official-width buttons. */
    layout?: SocialAuthLayout;
    className?: string;
}

const AUTH_ASSETS = {
    // Round badges — used in the compact (circular) layout.
    googleIcon: '/assets/auth/google-icon-round.svg',
    googleIcon2x: '/assets/auth/google-icon-round@2x.png',
    appleIcon: '/assets/auth/apple-icon-round.svg',
    // Bare logo marks — used in the custom stacked buttons so both providers
    // share one layout and their icons/text align (the old full-lockup SVGs
    // baked icon + text + bg into the image, so their insets couldn't be matched).
    googleMark: '/assets/auth/google-g.svg',
    appleMark: '/assets/auth/apple-logo.svg',
} as const;

/**
 * Per-provider styling for the custom stacked buttons. Colors follow each
 * vendor's brand requirements (Google: white + #747775 hairline; Apple: black +
 * white logo/text) — these intentionally override the app palette, as third-party
 * sign-in buttons must. See Apple's "Creating a custom Sign in with Apple button".
 */
const STACKED_STYLE = {
    google: { container: 'bg-white text-[#1f1f1f] border border-[#747775]', mark: AUTH_ASSETS.googleMark },
    apple: { container: 'bg-black text-white border border-black', mark: AUTH_ASSETS.appleMark },
} as const;

const COMPACT_SIZE = 52;

export const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({
    layout = 'stacked',
    className = '',
}) => {
    const { loginWithGoogle, loginWithApple } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProvider, setLoadingProvider] = useState<'google' | 'apple' | null>(null);
    const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

    const runSocialLogin = async (provider: 'google' | 'apple') => {
        setIsLoading(true);
        setLoadingProvider(provider);
        try {
            if (provider === 'google') {
                await loginWithGoogle();
            } else {
                await loginWithApple();
            }
        } catch {
            // AuthContext sets error for display by parent
        } finally {
            setIsLoading(false);
            setLoadingProvider(null);
        }
    };

    if (layout === 'compact') {
        return (
            <div className={`flex items-center justify-center gap-5 ${className}`}>
                <CompactProviderButton
                    provider="google"
                    label="Sign in with Google"
                    imageSrc={AUTH_ASSETS.googleIcon}
                    imageSrcSet={`${AUTH_ASSETS.googleIcon} 1x, ${AUTH_ASSETS.googleIcon2x} 2x`}
                    disabled={isLoading}
                    loading={loadingProvider === 'google'}
                    onClick={() => runSocialLogin('google')}
                />
                {!isAndroidNative && (
                    <CompactProviderButton
                        provider="apple"
                        label="Sign in with Apple"
                        imageSrc={AUTH_ASSETS.appleIcon}
                        disabled={isLoading}
                        loading={loadingProvider === 'apple'}
                        onClick={() => runSocialLogin('apple')}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={`space-y-3 ${className}`}>
            <StackedProviderButton
                provider="google"
                label="Sign in with Google"
                disabled={isLoading}
                loading={loadingProvider === 'google'}
                onClick={() => runSocialLogin('google')}
            />
            {!isAndroidNative && (
                <StackedProviderButton
                    provider="apple"
                    label="Sign in with Apple"
                    disabled={isLoading}
                    loading={loadingProvider === 'apple'}
                    onClick={() => runSocialLogin('apple')}
                />
            )}
        </div>
    );
};

interface ProviderButtonProps {
    provider: 'google' | 'apple';
    label: string;
    imageSrc: string;
    imageSrcSet?: string;
    disabled: boolean;
    loading: boolean;
    onClick: () => void;
}

/**
 * Custom (composed) provider button: one shared layout box for every provider, so
 * the logo marks line up across buttons and the label sits in the same place.
 * The mark is absolutely positioned at a fixed inset (identical for both providers
 * → icons align) and the label is centered.
 */
const StackedProviderButton: React.FC<Omit<ProviderButtonProps, 'imageSrc' | 'imageSrcSet'>> = ({
    provider,
    label,
    disabled,
    loading,
    onClick,
}) => {
    const style = STACKED_STYLE[provider];
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={loading ? 'Signing in…' : label}
            className={`relative w-full h-12 rounded-full flex items-center justify-center font-sans text-[15px] font-medium ${style.container} transition-opacity hover:opacity-90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foliage`}
        >
            <img
                src={style.mark}
                alt=""
                aria-hidden="true"
                className="absolute left-4 w-5 h-5 object-contain pointer-events-none"
                draggable={false}
            />
            <span>{loading ? 'Signing in…' : label}</span>
        </button>
    );
};

const CompactProviderButton: React.FC<ProviderButtonProps> = ({
    label,
    imageSrc,
    imageSrcSet,
    disabled,
    loading,
    onClick,
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={loading ? 'Signing in…' : label}
        className="rounded-full overflow-hidden transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foliage shadow-md"
        style={{ width: COMPACT_SIZE, height: COMPACT_SIZE }}
    >
        <img
            src={imageSrc}
            srcSet={imageSrcSet}
            alt=""
            className="w-full h-full object-cover pointer-events-none"
            draggable={false}
        />
    </button>
);
